import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown } from 'lucide-react';
import { useUser, useWorkspace } from "../../lib/context";
import { Dropdown } from "antd";

import UserProfileNavDrawer from "../UuiNavigation/UserProfileNavDrawer";
import WorkspaceDrawer from "../UuiNavigation/WorkspaceDrawer";
import Graph8Logo from "../Icons/Graph8Logo";

interface User {
  name?: string;
  email?: string;
}

interface UuiNavBarProps {
  user: User;
}

function abbr(user: User) {
  return (
    (user?.name)
      ?.split(" ")
      .filter((part) => part?.length > 0)
      .map((part) => part?.[0])
      .join("")
      .toUpperCase()
  );
}

const CreditBalanceCard: React.FC = () => {
  const workspace = useWorkspace();
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBalance = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch('/api/uui/credit-balance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ identifier: workspace.slugOrId }),
        });
        if (!response.ok) {
          console.error('Failed to fetch balance, status:', response.status);
          setError(true);
          setBalance(0);
          setLoading(false);
          return;
        }
        const data = await response.json();
        setBalance(data.credit_balance ?? 0);
      } catch (err) {
        console.error('Error fetching credit balance:', err);
        setError(true);
        setBalance(0);
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [workspace.slugOrId]);

  const formatBalance = (value: number | null) => {
    if (value === null || value === undefined) {
      return '0';
    }
    return value.toLocaleString('en-US');
  };

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--graph8-surface-element)] rounded-md border border-gray-300 dark:border-gray-800">
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className="lucide lucide-coins w-4 h-4 text-[var(--graph8-primary)]"
      >
        <circle cx="8" cy="8" r="6"></circle>
        <path d="M18.09 10.37A6 6 0 1 1 10.34 18"></path>
        <path d="M7 6h1v4"></path>
        <path d="m16.71 13.88.7.71-2.82 2.82"></path>
      </svg>
      {loading ? (
        <span className="text-[var(--graph8-text-muted)] text-sm">Loading...</span>
      ) : (
        <>
          <span className={`text-sm font-medium ${error ? 'text-red-400' : 'text-[var(--graph8-text)]'}`}>
            {formatBalance(balance)}
          </span>
          <span className="text-[var(--graph8-text-muted)] text-xs">credits</span>
        </>
      )}
    </div>
  );
};

const UserProfileButton: React.FC<{}> = () => {
  const user = useUser();
  return (
    <Dropdown
      dropdownRender={() => (
        <UserProfileNavDrawer
          user={{
            email: user.email,
            name: user.name,
          }}
        />
      )}
      placement="bottomLeft"
      arrow
      trigger={["click"]}
    >
      <span>
        <div className="flex cursor-pointer items-center gap-4 text-[var(--graph8-text)]">
          <div className="flex h-8 w-8 min-w-8 items-center justify-center rounded-full font-bold bg-[var(--graph8-surface-element)] text-[var(--graph8-text)] shadow-lg tracking-tight">
            {abbr(user)}
          </div>
          <div className="flex flex-col">
            <span className="hidden max-w-[250px] overflow-hidden sm:block text-sm whitespace-nowrap">
              {user?.email ?? user?.name ?? "Nameless User"}
            </span>
          </div>
          <ChevronDown size={18} className="text-[var(--graph8-text)]" />
        </div>
      </span>
    </Dropdown>
  );
};

const UuiNavBar: React.FC<UuiNavBarProps> = ({ user }) => {
  return (
    <nav className="fixed z-40 flex h-[73px] w-full items-center justify-between bg-default border-b border-gray-300 dark:border-gray-800 px-4 text-[var(--graph8-text)] sm:relative sm:p-4">
      <span className="z-50 flex items-center justify-center">
        <Link href="https://uui.cience.com">
          <div className="flex">
            <Graph8Logo className="max-w-[21px] max-h-[32px]" />
          </div>
        </Link>
      </span>

        <div className="z-10 flex items-center gap-10 self-center" data-testid="user-dropdown-trigger">
        <CreditBalanceCard />
        <WorkspaceDrawer />
        <UserProfileButton />
      </div>
    </nav>
  );
};

export default UuiNavBar;
